import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, FileText, TrendingUp, Users, Target, Filter, Search, ChevronRight, ExternalLink } from 'lucide-react';
import { analysisItems, getAnalysisCategories } from '../../data/centralizedData';

const Analysis = ({ onSectionChange }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const categories = getAnalysisCategories();

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'in-progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'planned': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'Portfolio Analysis': return BarChart3;
      case 'Member Analytics': return Users;
      case 'Market Analysis': return TrendingUp;
      case 'Methodology': return Target;
      case 'Pipeline Analysis': return FileText;
      default: return FileText;
    }
  };

  const filteredAnalyses = analysisItems.filter(analysis => {
    const matchesCategory = selectedCategory === 'all' || analysis.category === selectedCategory;
    const matchesSearch = searchTerm === '' ||
      analysis.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      analysis.executiveSummary.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  const handleAnalysisClick = (analysisId) => {
    // Navigate to the specific analysis route
    navigate(`/analysis/${analysisId}`);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Post-Meeting Analysis</h2>
          <p className="text-gray-600 mt-1">Follow-up analysis, deep dives, and strategic insights</p>
        </div>
        <div className="text-sm text-gray-500">
          {filteredAnalyses.length} {filteredAnalyses.length === 1 ? 'analysis' : 'analyses'}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search analyses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-600" />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Analysis Cards */}
      <div className="space-y-6">
        {filteredAnalyses.map(analysis => {
          const CategoryIcon = getCategoryIcon(analysis.category);
          
          return (
            <div 
              key={analysis.id}
              onClick={() => handleAnalysisClick(analysis.id)}
              className="bg-white rounded-xl border shadow-sm hover:shadow-md transition-all cursor-pointer group"
            >
              <div className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CategoryIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors mb-2">
                        {analysis.title}
                      </h3>
                      <div className="flex items-center space-x-3 text-sm">
                        <span className={`px-3 py-1 rounded-full border ${getStatusColor(analysis.status)}`}>
                          {analysis.status}
                        </span>
                        <div className="text-gray-500">{analysis.category}</div>
                        <div className="text-sm text-gray-500">{analysis.date}</div>
                      </div>
                    </div>
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                </div>
                
                <p className="text-gray-700 leading-relaxed mb-4">{analysis.executiveSummary}</p>

                <div className="flex items-center justify-end">
                  <div className="flex items-center space-x-2 text-blue-600 font-medium text-sm">
                    <span>View Analysis</span>
                    <ExternalLink className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredAnalyses.length === 0 && (
        <div className="bg-white p-12 rounded-xl border shadow-sm text-center">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No analyses found</h3>
          <p className="text-gray-600">
            Try adjusting your search terms or category filter to find relevant analyses.
          </p>
        </div>
      )}
    </div>
  );
};

export default Analysis;